import React, { useState, useMemo } from 'react';
import { LineChart, XAxis, YAxis, CartesianGrid, Line, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { AnalyticsTooltip } from './common/AnalyticsTooltip';

const StatCard = ({ title, value, children }) => (
    <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
        <h4 className="text-sm font-semibold font-nunito text-gray-600 mb-1">{title}</h4>
        {value && <p className="text-lg font-bold font-nunito text-gray-800">{value}</p>}
        <div className="font-nunito">{children}</div>
    </div>
);

export const AnalyticsChargingSelection = ({
    station,
    onSelect,
    isSelected
}) => {
    const [showPopup, setShowPopup] = useState(false);

    const handleClick = () => {
        // Toggle selection - if already selected, unselect it by passing null
        onSelect(isSelected ? null : station.cs_id);
    };

    const { chartData, isUtilized } = useMemo(() => {
        const isUtilized = station.is_utilized;
        const scheduleData = Object.entries(station.charging_schedule || {}).map(([timePeriod, data]) => {
            const timeIndex = parseInt(timePeriod, 10);
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            return { timeOfDay, totalLoad: data.total_load || 0, electricityRate: data.electricity_rate || 0 };
        });
        return { chartData: { scheduleData }, isUtilized };
    }, [station]);

    // Format currency values
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value);
    };

    // Helper function to check if a string is a valid URL
    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    // Don't render non-utilized stations if they should be filtered out
    if (!isUtilized && station.hideUnused) {
        return null;
    }

    return (
        <>
            <button 
                className={`w-full p-4 border rounded-lg text-left transition-all duration-300 flex flex-col relative
                    ${isSelected ? "bg-blue-50 border-blue-500 shadow-md" : "bg-white hover:bg-gray-50 border-gray-200"}
                    ${isUtilized ? "border-l-4 border-l-green-500" : "border-l-4 border-l-red-500"}
                `}
                onClick={handleClick}
            >
                <span className="font-bold text-gray-800 text-lg mb-2 font-nunito">{station.name}</span>
                <div className="flex flex-col gap-1 text-sm text-gray-600">
                    <span className="flex items-center">
                        Status: 
                        <span className={`font-semibold ml-2 ${isUtilized ? 'text-green-600' : 'text-red-600'}`}>
                            {isUtilized ? "Utilized" : "Not Utilized"}
                        </span>
                    </span>
                    <span className="font-nunito">Energy Delivered: {station.total_energy_delivered?.toFixed(2)} kWh</span>
                    <span className="font-nunito">Daily Cost: ${station.total_daily_cost?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <button 
                    className="mt-3 py-1 px-3 bg-blue-500 text-white text-xs font-semibold rounded-md hover:bg-blue-600 self-start" 
                    onClick={(e) => { e.stopPropagation(); setShowPopup(true); }}
                >
                    Show Details
                </button>
            </button>

            {showPopup && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowPopup(false)}
                >
                    <div 
                        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-xl font-bold text-gray-800">{`${station.name} Details`}</h3>
                            <button onClick={() => setShowPopup(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                <StatCard title="Status" value={isUtilized ? 'Utilized' : 'Not Utilized'} />
                                <StatCard title="Energy Delivered" value={`${station.total_energy_delivered?.toFixed(2)} kWh`} />
                                <StatCard title="Total Daily Cost" value={`$${station.total_daily_cost?.toLocaleString(undefined, {minimumFractionDigits: 2})}`} />
                            </div>
                            
                            {chartData.scheduleData.length > 0 && (
                                 <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">Charging Load Profile (kW)</h4>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={chartData.scheduleData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="charging" />} />
                                            <Legend />
                                            <Line type="monotone" dataKey="totalLoad" name="Total Load" stroke="#8884d8" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};